import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import AddAssetDialog from '../components/AddAssetDialog';
import InvestmentNameCombobox from '../components/InvestmentNameCombobox';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { formatCurrency } from '../lib/format';
import { convertedCost, convertedMarketValue, isOffshoreRow, fxForSource } from '../lib/currency';
import { downloadTemplate } from '../lib/excel';
import api from '../lib/api';
import { v4 as uuid } from '../lib/uuid';
import {
  Plus, Trash2, Copy, Save, Download, CheckCircle2, CircleDashed, Lock, Unlock,
  Calendar, FileType2, Paperclip, ClipboardPaste, Layers, Pencil, Sparkles, Share2,
} from 'lucide-react';
import { useSort, SortableTh } from '../components/SortableTable';
import PasteRowsDialog from '../components/PasteRowsDialog';
import FxRateStrip from '../components/FxRateStrip';
import AiImportDialog from '../components/AiImportDialog';
import TableSearch, { rowMatches } from '../components/TableSearch';
import NewReportingDateDialog from '../components/data-entry/NewReportingDateDialog';
import EditReportingDateDialog from '../components/data-entry/EditReportingDateDialog';
import CopyFromPreviousDialog from '../components/data-entry/CopyFromPreviousDialog';
import AllSourcesDownloadDialog from '../components/data-entry/AllSourcesDownloadDialog';
import LastEditedByBadge from '../components/data-entry/LastEditedByBadge';

const ALL_SOURCES = '__all__';

const BLANK_ROW = () => ({
  id: uuid(),
  institution: '',
  holder: '',
  investmentName: '',
  mainAssetClass: '',
  subAssetClass: '',
  marketCap: '',
  geography: '',
  cost: 0,
  marketValue: 0,
});

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fileKind = (type, name) => {
  const n = (name || '').toLowerCase();
  if (type === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (n.endsWith('.xlsx') || n.endsWith('.xls') || type?.includes('spreadsheet')) return 'xlsx';
  return 'file';
};

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function DataEntry() {
  const { snapshotId, sourceId: sourceIdParam } = useParams();
  const sourceId = sourceIdParam === 'all' ? ALL_SOURCES : sourceIdParam;
  const navigate = useNavigate();
  const {
    snapshots: allSnapshots, sources: allSources, dropdowns, assets,
    saveHoldings, getHoldings, addSnapshot, deleteSnapshot, updateSnapshot,
    isPeriodLocked, isSourceComplete, setSourceComplete, setPeriodLocked,
    addBackup, removeBackup, getBackups,
    getFxRate, fxRates, holdingsMeta = {},
  } = useApp();

  const snapshots = allSnapshots || [];
  const allSourcesRaw = allSources || [];

  const [pickedSnap, setPickedSnap] = useState(snapshotId || snapshots[snapshots.length - 1]?.id || '');
  const [pickedSource, setPickedSource] = useState(sourceId || allSourcesRaw[0]?.id || '');
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [newSnapOpen, setNewSnapOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [editSnapOpen, setEditSnapOpen] = useState(false);
  const [editSnapId, setEditSnapId] = useState(null);
  const [editSnapDate, setEditSnapDate] = useState('');
  const [copyDialog, setCopyDialog] = useState(false);
  const [copyFromId, setCopyFromId] = useState('');
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [addAssetSeed, setAddAssetSeed] = useState({});
  const [creatingRowId, setCreatingRowId] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [allSourcesDownloadOpen, setAllSourcesDownloadOpen] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);
  const [gridQuery, setGridQuery] = useState('');
  const [backupQuery, setBackupQuery] = useState('');
  const backupInputRef = useRef(null);

  // Pre-compute picked snap to derive source visibility (own vs shared)
  const pickedSnapPreview = snapshots.find((s) => s.id === pickedSnap);
  const pickedOwnerId = pickedSnapPreview?.shared_from?.user_id || null;
  // Visible sources: own when on own snap, owner's-shared when on shared snap
  const sources = useMemo(() => {
    if (!pickedOwnerId) return allSourcesRaw.filter((s) => !s.shared_from);
    return allSourcesRaw.filter((s) => s.shared_from && s.shared_from.user_id === pickedOwnerId);
  }, [allSourcesRaw, pickedOwnerId]);

  const snap = snapshots.find((s) => s.id === pickedSnap);
  const source = sources.find((s) => s.id === pickedSource);
  const isAllView = pickedSource === ALL_SOURCES;

  // Sharing context: are we on someone else's snapshot? What role do we have?
  const isShared = !!snap?.shared_from;
  const sharedRole = isShared ? (snap.shared_role || 'view') : null;
  const canEditShared = isShared && sharedRole === 'edit';

  // Snapshot may change — if the picked source isn't visible under the new snap, pick the first valid one
  useEffect(() => {
    if (isAllView) return;
    if (!sources.length) return;
    if (!pickedSource || !sources.find((s) => s.id === pickedSource)) {
      const firstId = sources[0].id;
      setPickedSource(firstId);
      navigate(`/entry/${pickedSnap}/${firstId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSnap, sources]);
  const periodLocked = isPeriodLocked(pickedSnap);
  const sourceComplete = !isAllView && isSourceComplete(pickedSnap, pickedSource);
  // Read-only when:
  //  - It's the All-Sources summary view
  //  - The period is locked OR source marked complete
  //  - It's someone else's portfolio AND we only have view rights
  const readonly = isAllView || periodLocked || sourceComplete || (isShared && !canEditShared);

  useEffect(() => {
    if (!pickedSnap) return;
    if (isAllView) {
      // All-sources is a read-only summary — no editable rows
      setRows([]);
      setDirty(false);
      setActiveRowIdx(null);
      return;
    }
    if (pickedSource) {
      const existing = getHoldings(pickedSnap, pickedSource);
      setRows(existing.length ? existing.map((r) => ({ ...r })) : [BLANK_ROW()]);
      setDirty(false);
      setActiveRowIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSnap, pickedSource, isAllView]);

  const setCell = (rowId, patch) => {
    if (readonly) return;
    setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, ...patch } : r));
    setDirty(true);
  };
  const addRow = () => { if (readonly) return; setRows((prev) => [...prev, BLANK_ROW()]); setDirty(true); };
  const removeRow = (id) => { if (readonly) return; setRows((prev) => prev.filter((r) => r.id !== id)); setDirty(true); };

  const save = async () => {
    if (readonly) return;
    const cleaned = rows.filter((r) => (r.investmentName || '').trim() || (r.institution || '').trim());
    try {
      await saveHoldings(pickedSnap, pickedSource, cleaned);
      setDirty(false);
      toast.success(`Saved ${cleaned.length} rows to ${source?.name}`);
    } catch (e) {
      // Surface the backend reason (423 locked / 403 read-only / etc.) instead of pretending the save worked.
      toast.error(`Save failed: ${e.message || 'Unknown error'}`);
    }
  };

  // Copy from previous (user picks which date)
  const openCopyDialog = () => {
    if (readonly) return;
    const currIdx = snapshots.findIndex((s) => s.id === pickedSnap);
    const firstPriorWithData = [...snapshots].slice(0, currIdx)
      .reverse()
      .find((s) => getHoldings(s.id, pickedSource).length > 0);
    setCopyFromId(firstPriorWithData?.id || '');
    setCopyDialog(true);
  };

  const confirmCopy = () => {
    if (!copyFromId) { toast.error('Pick a reporting date to copy from'); return; }
    const copied = getHoldings(copyFromId, pickedSource).map((r) => ({ ...r, id: uuid() }));
    if (copied.length === 0) { toast.error('No rows in that date for this source'); setCopyDialog(false); return; }
    setRows(copied);
    setDirty(true);
    setCopyDialog(false);
    const fromSnap = snapshots.find((s) => s.id === copyFromId);
    toast.success(`Copied ${copied.length} rows from ${longDate(fromSnap?.date)}`);
  };

  const handleDownloadCurrent = () => {
    // Backend generates the xlsx using server-side openpyxl; cookies are sent automatically.
    const url = api.excelTemplateUrl(pickedSnap, pickedSource);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${source?.name || 'source'}_${snap?.date || ''}.xlsx`;
    a.click();
  };

  // Backup
  const onBackupPicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB per backup in this mockup'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      addBackup(pickedSnap, pickedSource, {
        id: uuid(),
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        dataUrl: reader.result,
      });
      toast.success(`Attached ${file.name}`);
    };
    reader.readAsDataURL(file);
  };
  const downloadBackup = (b) => {
    const a = document.createElement('a'); a.href = b.dataUrl; a.download = b.name; a.click();
  };
  const backups = getBackups(pickedSnap, pickedSource);

  // Snapshot create
  const createSnapshot = async () => {
    if (!newDate) { toast.error('Pick a reporting date'); return; }
    try {
      const id = await addSnapshot(newDate);
      setPickedSnap(id);
      setNewSnapOpen(false);
      setNewDate('');
      // Find the newly-added snapshot to surface its derived label in the toast
      const created = snapshots.find((s) => s.id === id);
      toast.success(`Reporting date ${created?.label || newDate} created`);
    } catch (e) {
      toast.error(e.message || 'Could not create');
    }
  };

  const openEditSnapshot = (s) => {
    if (isPeriodLocked(s.id)) {
      toast.error(`Reporting date ${longDate(s.date)} is locked — unlock to edit`);
      return;
    }
    setEditSnapId(s.id);
    setEditSnapDate(s.date);
    setEditSnapOpen(true);
  };

  const commitEditSnapshot = async () => {
    if (!editSnapDate) { toast.error('Pick a date'); return; }
    try {
      const updated = await updateSnapshot(editSnapId, { date: editSnapDate });
      toast.success(`Reporting date updated to ${updated.label}`);
      setEditSnapOpen(false);
      setEditSnapId(null);
      setEditSnapDate('');
    } catch (e) {
      toast.error(e.message || 'Could not update');
    }
  };

  // Locks
  const toggleSourceComplete = () => {
    if (periodLocked) return;
    setSourceComplete(pickedSnap, pickedSource, !sourceComplete);
    toast.success(sourceComplete ? `${source.name} re-opened` : `${source.name} marked complete`);
  };
  const togglePeriodLock = () => {
    if (periodLocked) {
      if (!confirm(`Unlock reporting date ${longDate(snap?.date)}? Sources can then be edited again.`)) return;
      setPeriodLocked(pickedSnap, false);
      toast.success('Unlocked');
    } else {
      if (!confirm(`Lock reporting date ${longDate(snap?.date)}? All sources become read-only. You can unlock later.`)) return;
      setPeriodLocked(pickedSnap, true);
      toast.success('Locked');
    }
  };

  // Autocomplete handlers
  const onPickAsset = (rowId, asset) => {
    setCell(rowId, {
      investmentName: asset.investmentName,
      mainAssetClass: asset.mainAssetClass || '',
      subAssetClass: asset.subAssetClass || '',
      geography: asset.geography || '',
    });
  };

  const onCreateNewFromRow = (rowId, query) => {
    const row = rows.find((r) => r.id === rowId);
    setCreatingRowId(rowId);
    setAddAssetSeed({
      investmentName: query,
      mainAssetClass: row?.mainAssetClass || '',
      subAssetClass: row?.subAssetClass || '',
      geography: row?.geography || '',
      institutions: row?.institution ? [row.institution] : [],
    });
    setAddAssetOpen(true);
  };

  const onAssetCreated = (asset) => {
    if (creatingRowId) {
      setCell(creatingRowId, {
        investmentName: asset.investmentName,
        mainAssetClass: asset.mainAssetClass,
        subAssetClass: asset.subAssetClass,
        geography: asset.geography,
      });
    }
    setCreatingRowId(null);
  };

  // Paste rows commit
  const commitPaste = ({ rows: pasted, mode, activeRowIdx: idx }) => {
    if (readonly) return;
    const withIds = pasted.map((r) => ({ id: uuid(), ...r }));
    setRows((prev) => {
      if (mode === 'replace') {
        if (idx === null) return withIds;
        return [...prev.slice(0, idx), ...withIds];
      }
      // add / insert
      if (idx === null) return [...prev, ...withIds];
      return [...prev.slice(0, idx), ...withIds, ...prev.slice(idx)];
    });
    setDirty(true);
    setActiveRowIdx(null);
    toast.success(`${mode === 'replace' ? 'Replaced with' : 'Added'} ${withIds.length} row${withIds.length === 1 ? '' : 's'}`);
  };

  // AI Import commit — auto-saves so the row stays attached to the source even if user navigates away.
  const commitAiImport = async ({ rows: extracted, mode }) => {
    if (readonly) return;
    const withIds = extracted.map((r) => ({ ...r, id: r.id || uuid() }));
    const next = mode === 'replace' ? withIds : [...rows.filter((r) =>
      (r.investmentName || '').trim() || (r.institution || '').trim()
    ), ...withIds];
    setRows(next);
    setActiveRowIdx(null);
    try {
      await saveHoldings(pickedSnap, pickedSource, next);
      setDirty(false);
      toast.success(`AI Import: ${mode === 'replace' ? 'replaced with' : 'added'} ${withIds.length} row${withIds.length === 1 ? '' : 's'} and saved`);
    } catch (e) {
      setDirty(true);
      toast.error(`Loaded ${withIds.length} rows but save failed: ${e.message}`);
    }
  };

  const fxStored = (!isAllView && pickedSource) ? (getFxRate(pickedSnap, pickedSource, { resolve: true }) || {}) : {};
  const fxCost = fxStored.usdInrCost || fxStored.usdInr || 0;
  const fxMv   = fxStored.usdInrMv   || fxStored.usdInr || 0;
  const fxObj  = { cost: fxCost, mv: fxMv };

  // ---------- All-Sources summary (read-only) ----------
  // Groups rows across every source by investmentName (case-insensitive) — falls back to
  // `institution` when the name is blank. Values are converted to INR using each row's
  // own source FX rate before summing.
  const allAgg = useMemo(() => {
    if (!isAllView || !pickedSnap) return { rows: [], cost: 0, mv: 0 };
    const groups = new Map();
    let totCost = 0, totMv = 0;
    sources.forEach((src) => {
      const sRows = getHoldings(pickedSnap, src.id) || [];
      const fx = fxForSource(fxRates, pickedSnap, src.id);
      sRows.forEach((r) => {
        const name = (r.investmentName || '').trim();
        const inst = (r.institution || '').trim();
        if (!name && !inst) return;
        const key = (name || inst).toLowerCase();
        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            investmentName: name,
            _institutions: new Set(),
            _holders: new Set(),
            _sources: new Set(),
            _sourceIds: new Set(),
            mainAssetClass: r.mainAssetClass || '',
            subAssetClass: r.subAssetClass || '',
            marketCap: r.marketCap || '',
            geography: r.geography || '',
            cost: 0, marketValue: 0,
            rowCount: 0,
          });
        }
        const g = groups.get(key);
        if (inst) g._institutions.add(inst);
        if (r.holder) g._holders.add(r.holder);
        g._sources.add(src.name);
        g._sourceIds.add(src.id);
        // Prefer non-empty metadata from any occurrence
        if (!g.mainAssetClass && r.mainAssetClass) g.mainAssetClass = r.mainAssetClass;
        if (!g.subAssetClass && r.subAssetClass) g.subAssetClass = r.subAssetClass;
        if (!g.marketCap && r.marketCap) g.marketCap = r.marketCap;
        if (!g.geography && r.geography) g.geography = r.geography;
        const cInr = convertedCost(r, fx);
        const mInr = convertedMarketValue(r, fx);
        g.cost += cInr;
        g.marketValue += mInr;
        g.rowCount += 1;
        totCost += cInr;
        totMv += mInr;
      });
    });
    const aggRows = Array.from(groups.values()).map((g) => ({
      ...g,
      institution: Array.from(g._institutions).join(', '),
      holder: Array.from(g._holders).join(', '),
      sources: Array.from(g._sources).join(', '),
      sourceCount: g._sourceIds.size,
    }));
    return { rows: aggRows, cost: totCost, mv: totMv };
  }, [isAllView, pickedSnap, sources, fxRates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Totals in INR (offshore cost & MV converted using their own rates)
  const totals = useMemo(() => {
    if (isAllView) return { cost: allAgg.cost, mv: allAgg.mv };
    return {
      cost: rows.reduce((a, r) => a + convertedCost(r, fxObj), 0),
      mv:   rows.reduce((a, r) => a + convertedMarketValue(r, fxObj), 0),
    };
  }, [rows, fxCost, fxMv, isAllView, allAgg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sortable entry grid + backups
  const gridSort = useSort({ key: null, dir: 'asc' });
  const sortedRows = gridSort.sortRows(rows, {
    institution: (r) => r.institution || '',
    holder: (r) => r.holder || '',
    investmentName: (r) => r.investmentName || '',
    mainAssetClass: (r) => r.mainAssetClass || '',
    subAssetClass: (r) => r.subAssetClass || '',
    marketCap: (r) => r.marketCap || '',
    geography: (r) => r.geography || '',
    cost: (r) => Number(r.cost) || 0,
    marketValue: (r) => Number(r.marketValue) || 0,
    pnl: (r) => (Number(r.marketValue) || 0) - (Number(r.cost) || 0),
  });
  // Separate sort state for the All-Sources aggregated grid
  const allSort = useSort({ key: 'investmentName', dir: 'asc' });
  const sortedAllRows = allSort.sortRows(allAgg.rows, {
    sources: (r) => r.sources || '',
    institution: (r) => r.institution || '',
    holder: (r) => r.holder || '',
    investmentName: (r) => r.investmentName || '',
    mainAssetClass: (r) => r.mainAssetClass || '',
    subAssetClass: (r) => r.subAssetClass || '',
    marketCap: (r) => r.marketCap || '',
    geography: (r) => r.geography || '',
    cost: (r) => Number(r.cost) || 0,
    marketValue: (r) => Number(r.marketValue) || 0,
    pnl: (r) => (Number(r.marketValue) || 0) - (Number(r.cost) || 0),
  });
  const backupsSort = useSort({ key: null, dir: 'asc' });
  const sortedBackups = backupsSort.sortRows(backups, {
    name: (b) => b.name,
    size: (b) => b.size,
    uploadedAt: (b) => b.uploadedAt,
  });

  // Per-source status for tabs
  const sourceStatus = (srcId) => {
    if (periodLocked) return 'locked';
    if (isSourceComplete(pickedSnap, srcId)) return 'complete';
    const rws = getHoldings(pickedSnap, srcId);
    if (rws.length === 0) return 'empty';
    return 'progress';
  };
  const statusChip = (status) => {
    if (status === 'locked') return { icon: <Lock size={11} />, cls: 'chip-neutral', text: 'Locked' };
    if (status === 'complete') return { icon: <CheckCircle2 size={11} />, cls: 'chip-positive', text: 'Complete' };
    if (status === 'progress') return { icon: <CircleDashed size={11} />, cls: 'chip-warning', text: 'In progress' };
    return { icon: <CircleDashed size={11} />, cls: 'chip-neutral', text: 'Empty' };
  };

  const switchSource = (newSrcId) => {
    if (dirty && !readonly) {
      if (confirm('You have unsaved changes. Save before switching source?')) save();
    }
    setPickedSource(newSrcId);
    const seg = newSrcId === ALL_SOURCES ? 'all' : newSrcId;
    navigate(`/entry/${pickedSnap}/${seg}`, { replace: true });
  };

  // Eligible snapshots for copy-from (anything except current)
  const copyOptions = snapshots.filter((s) => s.id !== pickedSnap);

  return (
    <>
      <PageHeader
        overline="Capture"
        title="Data Entry"
        description="Capture holdings for a reporting date. Switch sources via the tabs below. Copy last period or fill inline — then mark the source complete. Lock the entire reporting date once everything is done."
        actions={
          <>
            {!isAllView && (
              <>
                <button className="btn-secondary" onClick={handleDownloadCurrent} disabled={!source} data-testid="download-current-button">
                  <Download size={14} /> Download
                </button>
                <button
                  className={`btn-primary ${readonly ? 'opacity-40 cursor-not-allowed' : ''}`}
                  onClick={save}
                  disabled={readonly}
                  data-testid="save-entry-button"
                >
                  <Save size={14} /> Save
                  {dirty && !readonly && <span className="w-1.5 h-1.5 rounded-full bg-white ml-1" />}
                </button>
              </>
            )}
            {isAllView && (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => setAllSourcesDownloadOpen(true)}
                  data-testid="download-all-sources-button"
                  title="Download a workbook with every source's rows for this reporting date"
                >
                  <Download size={14} /> Download Excel
                </button>
                <span className="chip chip-neutral" data-testid="all-sources-readonly-chip">
                  <Layers size={11} /> Read-only · summary
                </span>
              </>
            )}
          </>
        }
      />

      {/* Reporting date bar */}
      <div className="border border-border bg-white p-5 mb-6" data-testid="reporting-date-bar">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 pr-5 border-r border-border">
            <div className="w-9 h-9 border border-border flex items-center justify-center bg-[#F6F6F4]">
              <Calendar size={16} className="text-[hsl(var(--primary))]" />
            </div>
            <div>
              <div className="overline">Reporting date</div>
              <div className="font-heading font-semibold text-[15px] tracking-tight flex items-center gap-2">
                {snap ? longDate(snap.date) : 'No reporting date selected'}
                {periodLocked && <span className="chip chip-neutral"><Lock size={10} /> Locked</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {snapshots.map((s) => {
              const locked = isPeriodLocked(s.id);
              const isActive = pickedSnap === s.id;
              const isSharedPill = !!s.shared_from;
              return (
                <div
                  key={s.id}
                  data-testid={`reporting-date-pill-${s.id}`}
                  onClick={() => setPickedSnap(s.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPickedSnap(s.id); } }}
                  title={isSharedPill ? `Shared by ${s.shared_from.email} (${s.shared_role})` : undefined}
                  className={`chip cursor-pointer transition-all group select-none ${isActive ? '' : 'hover:bg-[#EFEFEA]'} ${(!locked && !isSharedPill) ? 'pr-1' : ''}`}
                  style={isActive ? { background: '#1A3626', color: '#fff', borderColor: '#1A3626' } : {}}
                >
                  {locked && <Lock size={10} />}
                  {isSharedPill && <Share2 size={10} />}
                  <span>{longDate(s.date)}</span>
                  {isSharedPill && (
                    <span
                      className={`text-[8px] font-bold uppercase tracking-wider px-1 ml-0.5 ${isActive ? 'text-white/80' : 'text-[#787872]'}`}
                    >
                      {s.shared_role}
                    </span>
                  )}
                  {!locked && !isSharedPill && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openEditSnapshot(s); }}
                      className={`ml-1 -mr-0.5 p-0.5 rounded transition-opacity ${
                        isActive ? 'opacity-70 hover:opacity-100 hover:bg-white/15' : 'opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:bg-[#E0E0DA]'
                      }`}
                      title={`Edit reporting date ${longDate(s.date)}`}
                      data-testid={`edit-reporting-date-${s.id}`}
                    >
                      <Pencil size={10} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <button className="btn-secondary" onClick={() => setNewSnapOpen(true)} data-testid="new-reporting-date-button">
            <Plus size={14} /> New reporting date
          </button>
          <button
            className={`btn-secondary ${periodLocked ? 'border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]' : ''} disabled:opacity-40 disabled:cursor-not-allowed`}
            onClick={togglePeriodLock}
            disabled={isShared}
            title={isShared ? `Only the owner (${snap?.shared_from?.email}) can lock this period` : undefined}
            data-testid="toggle-period-lock-button"
          >
            {periodLocked ? <><Unlock size={14} /> Unlock reporting date</> : <><Lock size={14} /> Lock reporting date</>}
          </button>
          <button
            className={`btn-secondary border-[#9B2C2C] text-[#9B2C2C] hover:bg-[#F5E4E4] disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={async () => {
              if (!snap) return;
              if (periodLocked) {
                toast.error(`Cannot delete a locked reporting date. Unlock ${longDate(snap.date)} first.`);
                return;
              }
              const totalRows = sources.reduce((acc, s) => acc + (getHoldings(snap.id, s.id) || []).length, 0);
              const msg =
                `Delete reporting date ${snap.label} (${longDate(snap.date)})?\n\n` +
                `This will permanently remove ${totalRows} holding row${totalRows === 1 ? '' : 's'} across all sources, ` +
                `plus locks, backups, and FX rate for this period.\n\nThis cannot be undone.`;
              if (!confirm(msg)) return;
              try {
                const res = await deleteSnapshot(snap.id);
                toast.success(`Deleted reporting date ${snap.label} (${res.rowsLost || totalRows} rows removed)`);
                // Pick the most recent remaining snapshot, if any
                const remaining = snapshots.filter((s) => s.id !== snap.id);
                setPickedSnap(remaining[remaining.length - 1]?.id || '');
              } catch (e) {
                toast.error(e.message || 'Delete failed');
              }
            }}
            disabled={!snap || periodLocked || isShared}
            data-testid="delete-reporting-date-button"
            title={!snap
              ? 'Pick a reporting date first'
              : isShared
                ? `Only the owner (${snap?.shared_from?.email}) can delete this reporting date`
                : periodLocked
                  ? `Reporting date ${longDate(snap.date)} is locked — unlock to delete`
                  : `Delete reporting date ${longDate(snap.date)} and all its data`}
          >
            <Trash2 size={14} /> Delete reporting date
          </button>
        </div>
      </div>

      {/* Shared-portfolio banner */}
      {isShared && (
        <div
          className={`mb-3 px-4 py-2.5 border flex items-center gap-3 text-[12px] ${
            canEditShared
              ? 'border-[#C9D9CC] bg-[#F1F6F2] text-[#1A3626]'
              : 'border-[#E5D9B5] bg-[#FDF8E8] text-[#7A5A1F]'
          }`}
          data-testid="shared-portfolio-banner"
        >
          <Share2 size={14} className="shrink-0" />
          <div className="flex-1">
            <div className="font-heading font-semibold">
              Shared portfolio · viewing {snap?.shared_from?.name || snap?.shared_from?.email}'s data
            </div>
            <div className="text-[11px] font-mono mt-0.5 opacity-80">
              {canEditShared
                ? `You have edit rights — changes save back to ${snap?.shared_from?.email}'s portfolio.`
                : `View-only access — only the owner or someone with edit rights can change this data.`}
            </div>
          </div>
          <span className={`chip ${canEditShared ? 'chip-positive' : 'chip-warning'}`}>{sharedRole}</span>
        </div>
      )}

      {/* FX rate strip — per-source (switches as user toggles source tabs); hidden in All-Sources summary */}
      {!isAllView && <FxRateStrip snapshotId={pickedSnap} sourceId={pickedSource} />}

      {/* Source tabs */}
      <div className="border border-border border-b-0 bg-white" data-testid="source-tabs">
        <div className="flex items-stretch overflow-x-auto">
          {/* Virtual "All Sources" summary tab — read-only aggregated view */}
          {(() => {
            const active = pickedSource === ALL_SOURCES;
            const totalRows = sources.reduce((a, s) => a + getHoldings(pickedSnap, s.id).length, 0);
            const uniqueNames = new Set();
            sources.forEach((s) => (getHoldings(pickedSnap, s.id) || []).forEach((r) => {
              const n = (r.investmentName || r.institution || '').trim().toLowerCase();
              if (n) uniqueNames.add(n);
            }));
            return (
              <div
                key="__all_sources__"
                className={`shrink-0 border-r border-border transition-colors flex flex-col gap-1 min-w-[190px] ${
                  active ? 'bg-[#F1F6F2] border-b-2 border-b-[hsl(var(--primary))]' : 'hover:bg-[#FAFAF8] border-b-2 border-b-transparent'
                }`}
              >
                <button
                  onClick={() => switchSource(ALL_SOURCES)}
                  data-testid="source-tab-all"
                  className="text-left px-5 pt-3 pb-1.5"
                >
                  <div className={`font-heading font-semibold text-[13px] tracking-tight flex items-center gap-1.5 ${active ? 'text-[hsl(var(--primary))]' : 'text-[#1C1C1C]'}`}>
                    <Layers size={12} /> All Sources
                  </div>
                </button>
                <div className="flex items-center gap-2 px-5 pb-2">
                  <span className="chip chip-neutral">Read-only</span>
                  <span className="text-[10px] text-[#787872] font-mono">
                    {uniqueNames.size} unique · {totalRows} rows
                  </span>
                </div>
              </div>
            );
          })()}
          {sources.map((s) => {
            const status = sourceStatus(s.id);
            const chip = statusChip(status);
            const active = pickedSource === s.id;
            const isComplete = isSourceComplete(pickedSnap, s.id);
            return (
              <div
                key={s.id}
                className={`shrink-0 border-r border-border transition-colors flex flex-col gap-1 min-w-[190px] ${
                  active ? 'bg-[#F6F6F4] border-b-2 border-b-[hsl(var(--primary))]' : 'hover:bg-[#FAFAF8] border-b-2 border-b-transparent'
                }`}
              >
                <button
                  onClick={() => switchSource(s.id)}
                  data-testid={`source-tab-${s.id}`}
                  className="text-left px-5 pt-3 pb-1.5"
                >
                  <div className={`font-heading font-semibold text-[13px] tracking-tight ${active ? 'text-[hsl(var(--primary))]' : 'text-[#1C1C1C]'}`}>
                    {s.name}
                  </div>
                </button>
                <div className="flex items-center gap-2 px-5 pb-2">
                  <span className={`chip ${chip.cls}`}>{chip.icon} {chip.text}</span>
                  <span className="text-[10px] text-[#787872] font-mono">
                    {getHoldings(pickedSnap, s.id).length} rows
                  </span>
                  {/* Per-tab lock / unlock control (disabled when the whole period is locked) */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (periodLocked) {
                        toast.error('Period is locked. Unlock the reporting date first.');
                        return;
                      }
                      try {
                        await setSourceComplete(pickedSnap, s.id, !isComplete);
                        toast.success(isComplete ? `${s.name} re-opened` : `${s.name} marked complete`);
                      } catch (err) {
                        toast.error(err.message || 'Could not update');
                      }
                    }}
                    disabled={periodLocked}
                    title={
                      periodLocked
                        ? 'Period is locked — unlock reporting date first'
                        : isComplete
                          ? `Re-open ${s.name}`
                          : `Mark ${s.name} complete`
                    }
                    className={`ml-auto p-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                      isComplete
                        ? 'text-[hsl(var(--positive))] hover:bg-[#E8F2EB]'
                        : 'text-[#787872] hover:bg-[#EFEFEA] hover:text-[hsl(var(--primary))]'
                    }`}
                    data-testid={`tab-lock-${s.id}`}
                  >
                    {isComplete ? <Unlock size={12} /> : <Lock size={12} />}
                  </button>
                </div>
                {/* Audit pill: last edited by (any user) */}
                {holdingsMeta[pickedSnap]?.[s.id]?.last_edited_by && (
                  <div className="px-5 pb-2 -mt-0.5">
                    <LastEditedByBadge entry={holdingsMeta[pickedSnap][s.id]} sourceId={s.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Source toolbar — Copy + Paste (hidden in All-Sources summary view) */}
      {source && !isAllView && (
        <div className="border border-border border-t-0 bg-[#FAFAF8] px-5 py-3 flex flex-wrap items-center gap-2" data-testid="source-toolbar">
          <button className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed" onClick={openCopyDialog} disabled={readonly} data-testid="toolbar-copy-previous"
            title={readonly ? (sourceComplete ? `${source.name} is marked complete — re-open to edit` : 'Period is locked') : 'Copy rows from a previous reporting date'}>
            <Copy size={13} /> Copy from previous reporting date
          </button>
          <button className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed" onClick={() => setPasteOpen(true)} disabled={readonly} data-testid="toolbar-paste-rows"
            title={readonly ? (sourceComplete ? `${source.name} is marked complete — re-open to edit` : 'Period is locked') : 'Paste rows from Excel / clipboard'}>
            <ClipboardPaste size={13} /> Paste rows
          </button>
          <button
            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => setAiImportOpen(true)}
            disabled={readonly}
            data-testid="toolbar-ai-import"
            title={readonly ? (sourceComplete ? `${source.name} is marked complete — re-open to edit` : 'Period is locked') : 'Upload a broker statement PDF — AI extracts rows'}
          >
            <Sparkles size={13} /> AI Import
          </button>
          <div className="flex-1" />
          {sourceComplete && (
            <span className="text-[11px] font-mono text-[hsl(var(--positive))]">
              ✓ {source.name} marked complete
            </span>
          )}
        </div>
      )}

      {readonly && periodLocked && (
        <div className="border border-[#E3C3C3] border-t-0 bg-[#FDF2F2] px-5 py-2.5 text-[12px] font-mono flex items-center gap-2 text-[#7A1F1F]" data-testid="readonly-banner">
          <Lock size={12} />
          Reporting date {longDate(snap?.date)} is locked. Unlock from the top bar to edit.
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-6 gap-px bg-border border border-border border-t-0">
        <div className="bg-white p-4">
          <div className="overline">Reporting date</div>
          <div className="font-mono text-sm mt-1">{snap ? longDate(snap.date) : '—'}</div>
        </div>
        <div className="bg-white p-4" data-testid="counter-periods">
          <div className="overline">Periods available</div>
          <div className="font-heading font-bold text-xl tabular-nums">{snapshots.length}</div>
        </div>
        <div className="bg-white p-4" data-testid="counter-complete">
          <div className="overline">Sources complete</div>
          {(() => {
            const completeCount = sources.filter((s) => isSourceComplete(pickedSnap, s.id)).length;
            const total = sources.length;
            const pct = total > 0 ? (completeCount / total) * 100 : 0;
            return (
              <>
                <div className="font-heading font-bold text-xl tabular-nums">
                  {completeCount}<span className="text-[#AAA] text-sm"> / {total}</span>
                </div>
                <div className="h-[3px] bg-[#EFEFEA] mt-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[hsl(var(--positive))] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </>
            );
          })()}
        </div>
        <div className="bg-white p-4">
          <div className="overline">{isAllView ? 'Unique investments' : 'Rows'}</div>
          <div className="font-heading font-bold text-xl tabular-nums">{isAllView ? allAgg.rows.length : rows.length}</div>
        </div>
        <div className="bg-white p-4">
          <div className="overline">Total cost</div>
          <div className="font-mono text-sm mt-1">{formatCurrency(totals.cost, 'INR', { mode: 'full' })}</div>
        </div>
        <div className="bg-white p-4">
          <div className="overline">Total mkt value</div>
          <div className={`font-mono text-sm mt-1 ${totals.mv - totals.cost >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
            {formatCurrency(totals.mv, 'INR', { mode: 'full' })}
          </div>
        </div>
      </div>

      {/* Grid search + grid (editable, per source) */}
      {!isAllView && (<>
      <div className="border border-border border-t-0 bg-white px-5 py-3" data-testid="grid-search-row">
        <TableSearch
          value={gridQuery}
          onChange={setGridQuery}
          placeholder="Filter rows by institution, holder, investment, class, strategy, geography…"
          testId="grid-search-input"
          className="mb-0"
          stats={gridQuery ? `${sortedRows.filter((r) => rowMatches(r, gridQuery)).length} / ${rows.length} rows` : null}
        />
      </div>

      <div className="border border-border border-t-0 bg-white overflow-visible overflow-x-auto">
        <table className="grid-table" data-testid="entry-grid">
          <thead>
            <tr>
              <th className="w-[44px]">#</th>
              <SortableTh sortKey="institution" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 180 }}>Institution</SortableTh>
              <SortableTh sortKey="holder" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 110 }}>Holder</SortableTh>
              <SortableTh sortKey="investmentName" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 260 }}>Investment Name</SortableTh>
              <SortableTh sortKey="mainAssetClass" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 150 }}>Main Asset Class</SortableTh>
              <SortableTh sortKey="subAssetClass" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 170 }}>Sub-Asset Class</SortableTh>
              <SortableTh sortKey="marketCap" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 130 }}>Strategy</SortableTh>
              <SortableTh sortKey="geography" sort={gridSort.sort} onSort={gridSort.onSort} style={{ minWidth: 140 }}>Geography</SortableTh>
              <SortableTh sortKey="cost" sort={gridSort.sort} onSort={gridSort.onSort} numeric style={{ minWidth: 160 }}>Cost</SortableTh>
              <SortableTh sortKey="marketValue" sort={gridSort.sort} onSort={gridSort.onSort} numeric style={{ minWidth: 160 }}>Market Value</SortableTh>
              <SortableTh sortKey="pnl" sort={gridSort.sort} onSort={gridSort.onSort} numeric>Unreal. P&amp;L</SortableTh>
              <th className="w-[44px]"> </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.filter((r) => rowMatches(r, gridQuery)).map((r, displayIdx) => {
              const idx = rows.findIndex((x) => x.id === r.id);
              const pnl = (Number(r.marketValue) || 0) - (Number(r.cost) || 0);
              const instListId = `insts-${pickedSource}-${idx}`;
              return (
                <tr
                  key={r.id}
                  onFocusCapture={() => setActiveRowIdx(idx)}
                  className={activeRowIdx === idx ? 'ring-1 ring-inset ring-[hsl(var(--primary))]/20' : ''}
                >
                  <td className="text-[#AAA] font-mono text-xs">{String(displayIdx + 1).padStart(2, '0')}</td>
                  <td>
                    <Input
                      value={r.institution || ''}
                      list={instListId}
                      disabled={readonly}
                      onChange={(e) => setCell(r.id, { institution: e.target.value })}
                      placeholder="Type or pick…"
                      className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]"
                      data-testid={`cell-institution-${idx}`}
                    />
                    <datalist id={instListId}>
                      {Array.from(new Set([
                        ...(source?.institutions || []),
                        ...((dropdowns?.institution) || []),
                      ])).sort().map((v) => <option key={v} value={v} />)}
                    </datalist>
                  </td>
                  <td>
                    <Select value={r.holder || ''} onValueChange={(v) => setCell(r.id, { holder: v })} disabled={readonly}>
                      <SelectTrigger className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]" data-testid={`cell-holder-${idx}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {dropdowns.holder.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <InvestmentNameCombobox
                      value={r.investmentName}
                      assets={assets}
                      disabled={readonly}
                      onChangeText={(t) => setCell(r.id, { investmentName: t })}
                      onSelectAsset={(a) => onPickAsset(r.id, a)}
                      onCreateNew={(q) => onCreateNewFromRow(r.id, q)}
                      testIdPrefix={`investment-${idx}`}
                    />
                  </td>
                  <td>
                    <Select value={r.mainAssetClass || ''} onValueChange={(v) => setCell(r.id, { mainAssetClass: v })} disabled={readonly}>
                      <SelectTrigger className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]" data-testid={`cell-mainAssetClass-${idx}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {dropdowns.mainAssetClass.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select value={r.subAssetClass || ''} onValueChange={(v) => setCell(r.id, { subAssetClass: v })} disabled={readonly}>
                      <SelectTrigger className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]" data-testid={`cell-subAssetClass-${idx}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {dropdowns.subAssetClass.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select value={r.marketCap || ''} onValueChange={(v) => setCell(r.id, { marketCap: v })} disabled={readonly}>
                      <SelectTrigger className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]" data-testid={`cell-marketCap-${idx}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {dropdowns.marketCap.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select value={r.geography || ''} onValueChange={(v) => setCell(r.id, { geography: v })} disabled={readonly}>
                      <SelectTrigger className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]" data-testid={`cell-geography-${idx}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {dropdowns.geography.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="num min-w-[180px]">
                    <div className="flex items-center gap-1 justify-end">
                      {isOffshoreRow(r) && <span className="text-[9px] font-mono text-[#B7791F]" title="Stored in USD">$</span>}
                      <Input
                        type="number" disabled={readonly}
                        value={r.cost ?? 0}
                        onChange={(e) => {
                          // When USD changes for an offshore row, drop the override so the
                          // ≈INR field re-derives until the user explicitly edits it again.
                          const patch = { cost: Number(e.target.value) };
                          if (isOffshoreRow(r)) patch.costInr = null;
                          setCell(r.id, patch);
                        }}
                        className="h-7 text-xs rounded-sm text-right font-mono tabular-nums border-transparent hover:border-border focus:border-[hsl(var(--primary))] min-w-[110px]"
                        data-testid={`cell-cost-${idx}`}
                      />
                    </div>
                    {isOffshoreRow(r) && (
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <span className="text-[9px] font-mono text-[#787872]">₹</span>
                        <Input
                          type="number" disabled={readonly}
                          value={
                            r.costInr != null && r.costInr !== ''
                              ? r.costInr
                              : (fxCost > 0 ? Math.round(Number(r.cost) * fxCost) : 0)
                          }
                          onChange={(e) => setCell(r.id, { costInr: e.target.value === '' ? null : Number(e.target.value) })}
                          className="h-6 text-[10px] rounded-sm text-right font-mono tabular-nums border-transparent hover:border-border focus:border-[hsl(var(--primary))] min-w-[110px]"
                          data-testid={`cell-cost-inr-${idx}`}
                          title="INR value (auto-derived from USD × cost rate; click to override)"
                        />
                      </div>
                    )}
                  </td>
                  <td className="num min-w-[180px]">
                    <div className="flex items-center gap-1 justify-end">
                      {isOffshoreRow(r) && <span className="text-[9px] font-mono text-[#B7791F]" title="Stored in USD">$</span>}
                      <Input
                        type="number" disabled={readonly}
                        value={r.marketValue ?? 0}
                        onChange={(e) => {
                          const patch = { marketValue: Number(e.target.value) };
                          if (isOffshoreRow(r)) patch.marketValueInr = null;
                          setCell(r.id, patch);
                        }}
                        className="h-7 text-xs rounded-sm text-right font-mono tabular-nums border-transparent hover:border-border focus:border-[hsl(var(--primary))] min-w-[110px]"
                        data-testid={`cell-marketValue-${idx}`}
                      />
                    </div>
                    {isOffshoreRow(r) && (
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <span className="text-[9px] font-mono text-[#787872]">₹</span>
                        <Input
                          type="number" disabled={readonly}
                          value={
                            r.marketValueInr != null && r.marketValueInr !== ''
                              ? r.marketValueInr
                              : (fxMv > 0 ? Math.round(Number(r.marketValue) * fxMv) : 0)
                          }
                          onChange={(e) => setCell(r.id, { marketValueInr: e.target.value === '' ? null : Number(e.target.value) })}
                          className="h-6 text-[10px] rounded-sm text-right font-mono tabular-nums border-transparent hover:border-border focus:border-[hsl(var(--primary))] min-w-[110px]"
                          data-testid={`cell-marketValue-inr-${idx}`}
                          title="INR value (auto-derived from USD × market value rate; click to override)"
                        />
                      </div>
                    )}
                  </td>
                  <td className={`num font-mono tabular-nums min-w-[160px] ${pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                    {(() => {
                      const pnlInr = convertedMarketValue(r, fxObj) - convertedCost(r, fxObj);
                      return (
                        <>
                          {pnlInr !== 0 && (pnlInr > 0 ? '+' : '')}{(pnlInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </>
                      );
                    })()}
                  </td>
                  <td>
                    {!readonly && (
                      <button onClick={() => removeRow(r.id)} className="btn-ghost text-[#9B2C2C] hover:bg-[#F5E4E4]" data-testid={`remove-row-${idx}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#F6F6F4' }}>
              <td colSpan={8} className="font-heading font-bold">
                {!readonly && (
                  <button onClick={addRow} className="btn-ghost" data-testid="add-row-button">
                    <Plus size={13} /> Add row
                  </button>
                )}
              </td>
              <td className="num font-heading font-bold">{formatCurrency(totals.cost, 'INR', { mode: 'full' })}</td>
              <td className="num font-heading font-bold">{formatCurrency(totals.mv, 'INR', { mode: 'full' })}</td>
              <td className={`num font-heading font-bold ${totals.mv - totals.cost >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                {totals.mv - totals.cost >= 0 ? '+' : ''}{formatCurrency(totals.mv - totals.cost, 'INR', { mode: 'full' })}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      </>)}

      {/* All-Sources summary grid (read-only) */}
      {isAllView && (<>
      <div className="border border-border border-t-0 bg-white px-5 py-3" data-testid="all-grid-search-row">
        <TableSearch
          value={gridQuery}
          onChange={setGridQuery}
          placeholder="Filter by investment, source, institution, class, strategy, geography…"
          testId="all-grid-search-input"
          className="mb-0"
          stats={gridQuery ? `${sortedAllRows.filter((r) => rowMatches(r, gridQuery)).length} / ${allAgg.rows.length} investments` : null}
        />
      </div>
      <div className="border border-[#DCE8DE] border-t-0 bg-[#F1F6F2] px-5 py-2 text-[11px] font-mono text-[#1A3626] flex items-center gap-2" data-testid="all-sources-note">
        <Layers size={12} />
        Read-only summary across all {sources.length} sources. Values in INR (offshore rows converted at each source's own FX rate). Edit individual sources in their tabs.
      </div>
      <div className="border border-border border-t-0 bg-white overflow-visible overflow-x-auto">
        <table className="grid-table" data-testid="all-sources-grid">
          <thead>
            <tr>
              <th className="w-[44px]">#</th>
              <SortableTh sortKey="sources" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 200 }}>Source</SortableTh>
              <SortableTh sortKey="institution" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 180 }}>Institution</SortableTh>
              <SortableTh sortKey="holder" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 110 }}>Holder</SortableTh>
              <SortableTh sortKey="investmentName" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 260 }}>Investment Name</SortableTh>
              <SortableTh sortKey="mainAssetClass" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 150 }}>Main Asset Class</SortableTh>
              <SortableTh sortKey="subAssetClass" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 170 }}>Sub-Asset Class</SortableTh>
              <SortableTh sortKey="marketCap" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 130 }}>Strategy</SortableTh>
              <SortableTh sortKey="geography" sort={allSort.sort} onSort={allSort.onSort} style={{ minWidth: 140 }}>Geography</SortableTh>
              <SortableTh sortKey="cost" sort={allSort.sort} onSort={allSort.onSort} numeric style={{ minWidth: 160 }}>Cost (₹)</SortableTh>
              <SortableTh sortKey="marketValue" sort={allSort.sort} onSort={allSort.onSort} numeric style={{ minWidth: 160 }}>Market Value (₹)</SortableTh>
              <SortableTh sortKey="pnl" sort={allSort.sort} onSort={allSort.onSort} numeric>Unreal. P&amp;L</SortableTh>
            </tr>
          </thead>
          <tbody>
            {sortedAllRows.filter((r) => rowMatches(r, gridQuery)).map((r, displayIdx) => {
              const pnl = r.marketValue - r.cost;
              return (
                <tr key={r.id} data-testid={`all-row-${r.id}`}>
                  <td className="text-[#AAA] font-mono text-xs">{String(displayIdx + 1).padStart(2, '0')}</td>
                  <td>
                    <div className="text-[12px] text-[#2B2B2B] font-mono leading-snug" title={r.sources}>
                      {r.sources}
                    </div>
                    {r.sourceCount > 1 && (
                      <span className="chip chip-warning text-[9px] mt-0.5" title={`Same investment held in ${r.sourceCount} different sources`}>
                        {r.sourceCount}× sources
                      </span>
                    )}
                  </td>
                  <td className="text-[12px] text-[#575757]">{r.institution || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px] text-[#575757]">{r.holder || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px] font-heading font-semibold">{r.investmentName || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px]">{r.mainAssetClass || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px]">{r.subAssetClass || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px]">{r.marketCap || <span className="text-[#AAA]">—</span>}</td>
                  <td className="text-[12px]">{r.geography || <span className="text-[#AAA]">—</span>}</td>
                  <td className="num font-mono tabular-nums text-[12px]">{formatCurrency(r.cost, 'INR', { mode: 'full' })}</td>
                  <td className="num font-mono tabular-nums text-[12px]">{formatCurrency(r.marketValue, 'INR', { mode: 'full' })}</td>
                  <td className={`num font-mono tabular-nums text-[12px] ${pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                    {pnl !== 0 && (pnl > 0 ? '+' : '')}{formatCurrency(pnl, 'INR', { mode: 'full' })}
                  </td>
                </tr>
              );
            })}
            {sortedAllRows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-6 py-10 text-center text-[#AAA] text-[13px]">
                  No holdings recorded across any source for this reporting date yet.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: '#F6F6F4' }}>
              <td colSpan={9} className="font-heading font-bold">Portfolio total ({allAgg.rows.length} investments across {sources.length} sources)</td>
              <td className="num font-heading font-bold">{formatCurrency(totals.cost, 'INR', { mode: 'full' })}</td>
              <td className="num font-heading font-bold">{formatCurrency(totals.mv, 'INR', { mode: 'full' })}</td>
              <td className={`num font-heading font-bold ${totals.mv - totals.cost >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                {totals.mv - totals.cost >= 0 ? '+' : ''}{formatCurrency(totals.mv - totals.cost, 'INR', { mode: 'full' })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      </>)}

      {/* Backups panel — hidden in the All-Sources summary */}
      {!isAllView && (
      <section className="mt-8" data-testid="backups-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Backups for {source?.name} · {longDate(snap?.date)}</div>
            <div className="text-[12px] text-[#575757] mt-1">
              Attach the original broker statement or any source document (PDF / Excel). Stored locally in this mockup; max 5&nbsp;MB per file.
            </div>
          </div>
          <button className="btn-secondary" onClick={() => backupInputRef.current?.click()} data-testid="attach-backup-button">
            <Paperclip size={14} /> Attach file
          </button>
        </div>
        <div className="border border-border bg-white">
          {backups.length === 0 ? (
            <div className="px-6 py-10 text-center text-[#AAA] text-[13px]">No backups attached yet.</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-border">
                <TableSearch
                  value={backupQuery}
                  onChange={setBackupQuery}
                  placeholder="Search backups by file name…"
                  testId="backups-search-input"
                  className="mb-0"
                />
              </div>
              <table className="grid-table">
              <thead>
                <tr>
                  <th className="w-[50px]">Type</th>
                  <SortableTh sortKey="name" sort={backupsSort.sort} onSort={backupsSort.onSort}>File</SortableTh>
                  <SortableTh sortKey="size" sort={backupsSort.sort} onSort={backupsSort.onSort} numeric className="w-[100px]">Size</SortableTh>
                  <SortableTh sortKey="uploadedAt" sort={backupsSort.sort} onSort={backupsSort.onSort} className="w-[180px]">Uploaded</SortableTh>
                  <th className="w-[180px]" />
                </tr>
              </thead>
              <tbody>
                {sortedBackups.filter((b) => rowMatches(b, backupQuery)).map((b) => {
                  const kind = fileKind(b.type, b.name);
                  return (
                    <tr key={b.id}>
                      <td>
                        <div className="w-8 h-8 border border-border flex items-center justify-center bg-[#F6F6F4]">
                          <FileType2 size={14} className={kind === 'pdf' ? 'text-[#9B2C2C]' : kind === 'xlsx' ? 'text-[hsl(var(--positive))]' : 'text-[hsl(var(--primary))]'} />
                        </div>
                      </td>
                      <td>
                        <div className="font-heading font-semibold text-[13px]">{b.name}</div>
                        <div className="text-[11px] text-[#787872] font-mono uppercase">{kind}</div>
                      </td>
                      <td className="num tabular-nums font-mono text-xs">{humanSize(b.size)}</td>
                      <td className="text-xs text-[#575757] font-mono">{new Date(b.uploadedAt).toLocaleString()}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost" onClick={() => downloadBackup(b)} data-testid={`download-backup-${b.id}`}>
                            <Download size={13} /> Download
                          </button>
                          <button
                            className="btn-ghost text-[#9B2C2C] hover:bg-[#F5E4E4]"
                            onClick={() => { if (confirm(`Remove ${b.name}?`)) removeBackup(pickedSnap, pickedSource, b.id); }}
                            data-testid={`remove-backup-${b.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      </section>
      )}

      <input
        ref={backupInputRef}
        type="file"
        accept=".pdf,.xlsx,.xls"
        className="hidden"
        onChange={onBackupPicked}
        data-testid="hidden-backup-input"
      />

      {/* Edit reporting date dialog */}
      <EditReportingDateDialog
        open={editSnapOpen}
        onOpenChange={setEditSnapOpen}
        date={editSnapDate}
        onDateChange={setEditSnapDate}
        onConfirm={commitEditSnapshot}
      />

      {/* New reporting date dialog */}
      <NewReportingDateDialog
        open={newSnapOpen}
        onOpenChange={setNewSnapOpen}
        date={newDate}
        onDateChange={setNewDate}
        onCreate={createSnapshot}
      />

      {/* Copy from previous dialog */}
      <CopyFromPreviousDialog
        open={copyDialog}
        onOpenChange={setCopyDialog}
        sourceName={source?.name}
        options={copyOptions}
        value={copyFromId}
        onValueChange={setCopyFromId}
        countRowsFn={(snapId) => getHoldings(snapId, pickedSource).length}
        onConfirm={confirmCopy}
      />

      {/* Shared Add Asset dialog */}
      <AddAssetDialog
        open={addAssetOpen}
        onOpenChange={(o) => { setAddAssetOpen(o); if (!o) setCreatingRowId(null); }}
        initial={addAssetSeed}
        onCreated={onAssetCreated}
      />

      {/* Paste rows dialog */}
      <PasteRowsDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        dropdowns={dropdowns}
        activeRowIdx={activeRowIdx}
        totalRows={rows.length}
        onCommit={commitPaste}
      />

      {/* AI Import dialog (PDF → Claude → preview → commit) */}
      <AiImportDialog
        open={aiImportOpen}
        onOpenChange={setAiImportOpen}
        snapshotId={pickedSnap}
        sourceId={pickedSource}
        sourceName={source?.name || ''}
        snapshotLabel={snap?.label || ''}
        existingRowCount={rows.filter((r) => (r.investmentName || '').trim() || (r.institution || '').trim()).length}
        onCommit={commitAiImport}
      />

      {/* All Sources Excel download options */}
      <AllSourcesDownloadDialog
        open={allSourcesDownloadOpen}
        onOpenChange={setAllSourcesDownloadOpen}
        snapshot={snap}
      />
    </>
  );
}
